import { useSearchParams } from "react-router-dom";
import { MessageCircle, Mail } from "lucide-react";
import { MaskedLines } from "@/components/MaskedLines";
import { Reveal } from "@/components/Reveal";
import { EnquiryForm } from "@/components/EnquiryForm";
import { PageLoader } from "@/components/PageLoader";
import { Seo } from "@/components/Seo";
import { useConfig } from "@/context/ConfigContext";
import { fmtPhone, waLink } from "@/lib/format";
import { TID } from "@/lib/testIds";

export default function Contact() {
  const { config, loading } = useConfig();
  const [params] = useSearchParams();
  const preset = params.get("package") || "";

  if (loading) return <PageLoader />;

  return (
    <>
      <Seo
        title="Start a Web Design Project — OFFCRAFT Studio"
        description="Tell OFFCRAFT about your project or message us on WhatsApp. We reply within one working day with a clear quote — no forms disappearing into a void."
      />
      <main className="mx-auto max-w-7xl px-6 pb-24 pt-28 lg:pt-36">
      <div className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-crimson">Contact</p>
          <MaskedLines
            ariaLabel="Tell us what you are building"
            delay={0.2}
            className="mt-6 font-display text-4xl font-extrabold leading-[0.95] tracking-tight text-ink sm:text-5xl lg:text-6xl"
            lines={[<>Let's build</>, <>something worth</>, <><span className="text-crimson">launching.</span></>]}
          />
          <p className="mt-6 max-w-md text-base leading-relaxed text-ink/60">
            Send the form and it lands directly in our studio dashboard — or go straight to WhatsApp with your package pre-filled.
          </p>

          <div className="mt-12 space-y-6">
            {config.whatsapp_number ? (
              <a
                data-testid="contact-info-whatsapp"
                href={waLink(config.whatsapp_number, "Hi OFFCRAFT, I'd like to talk about a project.")}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-4 border border-forest/10 bg-surface p-5 transition-colors hover:border-emerald-400/50"
              >
                <MessageCircle className="h-5 w-5 text-emerald-600" />
                <span>
                  <span className="block font-mono text-[11px] uppercase tracking-[0.25em] text-ink/60">WhatsApp</span>
                  <span className="mt-1 block font-display text-lg font-bold text-ink transition-colors group-hover:text-emerald-600">{fmtPhone(config.whatsapp_number)}</span>
                </span>
              </a>
            ) : null}
            {config.contact?.email ? (
              <a
                data-testid="contact-info-email"
                href={`mailto:${config.contact.email}`}
                className="group flex items-center gap-4 border border-forest/10 bg-surface p-5 transition-colors hover:border-ink/100"
              >
                <Mail className="h-5 w-5 text-crimson" />
                <span>
                  <span className="block font-mono text-[11px] uppercase tracking-[0.25em] text-ink/60">Email</span>
                  <span className="mt-1 block font-display text-lg font-bold text-ink transition-colors group-hover:text-crimson">{config.contact.email}</span>
                </span>
              </a>
            ) : null}
            {config.contact?.response_time ? (
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink/60">{config.contact.response_time}</p>
            ) : null}
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="border border-forest/10 bg-surface p-6 sm:p-10">
              <EnquiryForm
                key={preset}
                packages={config.packages || []}
                maintenanceName={config.maintenance_plan?.name}
                whatsappNumber={config.whatsapp_number}
                defaultPackageId={preset}
              />
            </div>
          </Reveal>
        </div>
      </div>
    </main>
    </>
  );
}
